import { SwapOutlined } from "@ant-design/icons";
import { Alert, Button, Col, DatePicker, Form, InputNumber, Modal, notification, Row, Typography } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../../api/calls";
import { IsBiweeklyBuildingUnit } from "../../../helpers";
import PartnerSelect from "../../Partners/PartnerSelect";
import BiweeklyTypeSelect from "../BiweeklyTypeSelect";
import RruleDaySelect from "../RruleDaySelect";

const BATCH_STATUS = {
  ACCEPTABLE: [200, 422],
};

const PartnerTransferModal = ({ selectedBuildingUnits, refresh }) => {
  const { t } = useTranslation();

  const [isOpen, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unitErrors, setUnitErrors] = useState({});
  const [touchedDateFrom, setTouchedDateFrom] = useState({});

  const [form] = Form.useForm();
  const dateTerminate = Form.useWatch("date_terminate", form);
  const isTerminateInPreviousMonth = dateTerminate && moment(dateTerminate).isBefore(moment().startOf("month"));

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setUnitErrors({});
      setTouchedDateFrom({});
    }
  }, [isOpen, form]);

  useEffect(() => {
    if (!dateTerminate) return;

    const defaults = {};
    selectedBuildingUnits.forEach((unit) => {
      if (!touchedDateFrom[unit.id]) {
        defaults[`building-${unit.id}--date_from`] = moment(dateTerminate).add(1, "days");
      }
    });
    form.setFieldsValue(defaults);
  }, [dateTerminate]);

  const describeFailures = (failedResults) => (
    <div>
      {failedResults.map((result) => {
        const unit = selectedBuildingUnits.find((u) => u.id === result.building_unit_id);
        const label = unit ? `${unit.street} ${unit.house_number}` : `#${result.building_unit_id}`;
        return (
          <div key={result.building_unit_id}>
            {label}: {result.error}
          </div>
        );
      })}
    </div>
  );

  const runTransfer = async (values) => {
    const dateTerminateStr = values.date_terminate.format("YYYY-MM-DD");
    const buildingUnitIds = selectedBuildingUnits.map((unit) => unit.id);

    setSubmitting(true);
    setUnitErrors({});

    try {
      const endResponse = await coreApi.post(
        "/building-units/end-cooperation",
        {
          building_unit_ids: buildingUnitIds,
          date_terminate: dateTerminateStr,
        },
        { validateStatus: (status) => BATCH_STATUS.ACCEPTABLE.includes(status) }
      );

      const endFailed = (endResponse.data.results || []).filter((result) => result.status === "failed");
      if (endFailed.length > 0) {
        setUnitErrors(Object.fromEntries(endFailed.map((result) => [result.building_unit_id, result.error])));
        notification.error({
          message: t("schedules.partnerTransfer.endCooperationFailedTitle"),
          description: describeFailures(endFailed),
          duration: 0,
        });
        return;
      }

      const buildingUnitsPayload = selectedBuildingUnits.map((unit) => ({
        building_unit_id: unit.id,
        days: values[`building-${unit.id}--days`],
        service_ratio: values[`building-${unit.id}--service_ratio`],
        biweekly_type: IsBiweeklyBuildingUnit(unit) ? values[`building-${unit.id}--biweeklyType`] ?? null : null,
        date_start: values[`building-${unit.id}--date_from`]?.format("YYYY-MM-DD") ?? null,
        date_to: values[`building-${unit.id}--date_to`]?.format("YYYY-MM-DD") ?? null,
      }));

      const startResponse = await coreApi.post(
        "/building-units/start-cooperation",
        {
          new_partner_id: values.new_partner_id,
          date_start: dateTerminateStr,
          building_units: buildingUnitsPayload,
        },
        { validateStatus: (status) => BATCH_STATUS.ACCEPTABLE.includes(status) }
      );

      const startFailed = (startResponse.data.results || []).filter((result) => result.status === "failed");
      if (startFailed.length > 0) {
        setUnitErrors(Object.fromEntries(startFailed.map((result) => [result.building_unit_id, result.error])));
        notification.error({
          message: t("schedules.partnerTransfer.startCooperationFailedTitle"),
          description: describeFailures(startFailed),
          duration: 0,
        });
        return;
      }

      notification.success({ message: t("schedules.partnerTransfer.success") });
      setOpen(false);
      refresh && refresh();
    } catch (error) {
      notification.error({
        message: t("common.error"),
        description: error?.response?.data?.message || error.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = (values) => {
    Modal.confirm({
      title: t("schedules.partnerTransfer.confirmTitle"),
      content: t("schedules.partnerTransfer.confirmContent", { count: selectedBuildingUnits.length }),
      okText: t("common.confirmAnswer"),
      cancelText: t("common.rejectAnswer"),
      onOk: () => runTransfer(values),
    });
  };

  return (
    <React.Fragment>
      <Button
        size={"small"}
        icon={<SwapOutlined />}
        onClick={() => setOpen(true)}
        style={{ marginRight: "15px" }}
        disabled={!selectedBuildingUnits?.length}
      >
        {t("schedules.partnerTransfer.button")}
      </Button>
      <Modal
        width={700}
        footer={null}
        title={t("schedules.partnerTransfer.title")}
        open={isOpen}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout={"vertical"} onFinish={handleFinish}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name={"date_terminate"}
                label={t("schedules.partnerTransfer.dateTerminate")}
                rules={[
                  {
                    required: true,
                    message: t("schedules.dateFromRequiredMessage"),
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) => current && current < moment().subtract(1, "month").startOf("month")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <PartnerSelect
                name={"new_partner_id"}
                label={t("schedules.partnerTransfer.newPartner")}
                required={true}
                validationMessage={t("schedules.validation.partner")}
              />
            </Col>
          </Row>

          {isTerminateInPreviousMonth && (
            <Alert
              type={"info"}
              showIcon
              message={t("schedules.partnerTransfer.previousMonthInfo", {
                month: moment(dateTerminate).format("MM/YYYY"),
              })}
            />
          )}

          {selectedBuildingUnits.map((unit, index) => (
            <Row
              key={unit.id}
              gutter={[16, 16]}
              style={{
                paddingTop: "20px",
                paddingBottom: "20px",
                borderBottom: "1px solid gainsboro",
              }}
            >
              <Col span={24}>
                <strong>
                  {index + 1}. {t("buildingUnits.street")}: {unit.street} {unit.house_number}
                </strong>
              </Col>
              {unitErrors[unit.id] && (
                <Col span={24}>
                  <Alert type={"error"} showIcon message={unitErrors[unit.id]} />
                </Col>
              )}
              <Col span={IsBiweeklyBuildingUnit(unit) ? 12 : 24}>
                <RruleDaySelect
                  hasFeedback={true}
                  isRequired={true}
                  rrule={null}
                  name={`building-${unit.id}--days`}
                />
              </Col>
              {IsBiweeklyBuildingUnit(unit) && (
                <Col span={12}>
                  <BiweeklyTypeSelect name={`building-${unit.id}--biweeklyType`} postChange={() => {}} required={true} />
                </Col>
              )}
              <Col xs={24} md={8}>
                <Form.Item
                  name={`building-${unit.id}--date_from`}
                  label={t("schedules.dateFrom")}
                  dependencies={["date_terminate"]}
                  rules={[
                    {
                      required: true,
                      message: t("schedules.dateFromRequiredMessage"),
                    },
                    {
                      warningOnly: true,
                      validator: (_, value) => {
                        const terminate = form.getFieldValue("date_terminate");
                        if (!value || !terminate || moment(value).isAfter(terminate, "day")) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t("schedules.partnerTransfer.dateFromBeforeTerminateWarning", {
                          date: moment(terminate).add(1, "days").format("DD.MM.YYYY"),
                        })));
                      },
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current) => current && dateTerminate && current <= moment(dateTerminate).endOf("day")}
                    onChange={() => setTouchedDateFrom((prev) => ({ ...prev, [unit.id]: true }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name={`building-${unit.id}--date_to`} label={t("schedules.dateTo")} initialValue={null}>
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name={`building-${unit.id}--service_ratio`}
                  label={t("schedules.serviceRatio")}
                  initialValue={0.4}
                  rules={[
                    {
                      required: true,
                      message: t("schedules.serviceRatioRequiredMessage"),
                    },
                  ]}
                >
                  <InputNumber style={{ width: "100%" }} max={0.99} min={0.0} step={0.01} />
                </Form.Item>
              </Col>
            </Row>
          ))}

          <Typography.Paragraph type={"secondary"} style={{ marginTop: "16px" }}>
            {t("schedules.partnerTransfer.description")}
          </Typography.Paragraph>

          <Row>
            <Col span={24}>
              <Button type={"primary"} htmlType={"submit"} block loading={submitting}>
                {t("common.save")}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default PartnerTransferModal;
